import * as THREE from "three";

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

/**
 * Equirect norm (0..1) from a world-space hit on an inner sphere built with
 * `SphereGeometry` + `scale(-1, 1, 1)` — matches `ptz.service.ts` applyFocus.
 */
export function normsFromSphereHitPoint(
  px: number,
  py: number,
  pz: number,
  uv?: { x: number; y: number },
): { normX: number; normY: number } | null {
  if (uv && Number.isFinite(uv.x) && Number.isFinite(uv.y)) {
    return { normX: clamp01(uv.x), normY: clamp01(1 - uv.y) };
  }
  const len = Math.hypot(px, py, pz);
  if (!Number.isFinite(len) || len < 1e-6) return null;
  const nx = px / len;
  const ny = py / len;
  const nz = pz / len;
  const thetaT = Math.acos(Math.max(-1, Math.min(1, ny)));
  const sinT = Math.sin(thetaT);
  let normX: number;
  if (Math.abs(sinT) < 1e-5) {
    normX = 0.5;
  } else {
    let phiU = Math.atan2(nz, nx);
    if (phiU < 0) phiU += Math.PI * 2;
    normX = phiU / (Math.PI * 2);
  }
  const normY = clamp01(thetaT / Math.PI);
  return { normX: clamp01(normX), normY };
}

/** World point on the inner sphere for equirect (normX, normY). */
export function worldOnSphereFromNorm(
  normX: number,
  normY: number,
  radius = 500,
): THREE.Vector3 {
  const uRow = clamp01(normX);
  const vRow = clamp01(normY);
  const phi = uRow * Math.PI * 2;
  const thetaT = vRow * Math.PI;
  const x0 = -radius * Math.cos(phi) * Math.sin(thetaT);
  const y0 = radius * Math.cos(thetaT);
  const z0 = radius * Math.sin(phi) * Math.sin(thetaT);
  return new THREE.Vector3(-x0, y0, z0);
}
